import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWordBank,
  createWords,
  updateWord,
  deleteWord,
} from "../api/word-bank.api";
import { useAuth } from "../features/auth";

const EMPTY_GROUPED = () => ({
  byScope: { default: [], district: [], school: [], custom: [] },
  byCreator: {},
});

/**
 * Remove word entries from a GroupedWordBank snapshot by their _id
 */
const removeWords = (data = EMPTY_GROUPED(), ids) => {
  const idSet = new Set(ids.map(String));

  const byScope = Object.fromEntries(
    Object.entries(data.byScope).map(([scope, words]) => [
      scope,
      words.filter((w) => !idSet.has(String(w._id))),
    ]),
  );

  const byCreator = Object.fromEntries(
    Object.entries(data.byCreator).map(([creator, words]) => [
      creator,
      words.filter((w) => !idSet.has(String(w._id))),
    ]),
  );

  return { byScope, byCreator };
};

/**
 * Insert word entries at the head of a GroupedWordBank snapshot
 */
const prependWords = (data = EMPTY_GROUPED(), newWords) => {
  const byScope = { ...data.byScope };
  const byCreator = { ...data.byCreator };

  for (const word of newWords) {
    byScope[word.scope] = [word, ...(byScope[word.scope] ?? [])];
    byCreator[word.createdBy] = [word, ...(byCreator[word.createdBy] ?? [])];
  }

  return { byScope, byCreator };
};

/**
 * Patch word entries in a GroupedWordBank snapshot
 */
const patchWord = (data = EMPTY_GROUPED(), id, patch) => {
  const apply = (words) =>
    words.map((w) => (String(w._id) === String(id) ? { ...w, ...patch } : w));

  const byScope = Object.fromEntries(
    Object.entries(data.byScope).map(([scope, words]) => [scope, apply(words)]),
  );

  const byCreator = Object.fromEntries(
    Object.entries(data.byCreator).map(([creator, words]) => [
      creator,
      apply(words),
    ]),
  );

  return { byScope, byCreator };
};

/**
 * @param {string} userId - Authenticated user's ID
 * @param {Object} [filters] - { scope, entityId, active, createdBy }
 */
export const useWordBank = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["word-bank", user._id];

  const wordBankQuery = useQuery({
    queryKey,
    queryFn: () => getWordBank(),
    enabled: !!user._id,
    // Keep previous data visible while background refetch runs
    keepPreviousData: true,
  });

  const createWordsMutation = useMutation({
    mutationFn: (wordsData) => createWords(wordsData),
    onMutate: async (wordsData) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      const optimisticWords = wordsData.map((w) => ({
        _id: `optimistic-${crypto.randomUUID()}`,
        word: w.word?.toLowerCase().trim(),
        tier: w.tier,
        scope: w.scope ?? "custom",
        entityId: w.entityId ?? user._id.toString(),
        createdBy: user._id,
        updatedBy: user._id,
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _optimistic: true,
      }));

      queryClient.setQueryData(queryKey, (old) =>
        prependWords(old ?? EMPTY_GROUPED(), optimisticWords),
      );

      return { previousData, optimisticWords };
    },
    onSuccess: ({ result }, _variables, context) => {
      const optimisticIds = context.optimisticWords.map((w) => w._id);

      queryClient.setQueryData(queryKey, (old) => {
        const withoutOptimistic = removeWords(old, optimisticIds);
        return prependWords(withoutOptimistic, result);
      });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateWordMutation = useMutation({
    mutationFn: ({ id, updates }) => updateWord(id, { updates }),
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) =>
        patchWord(old, id, {
          ...updates,
          updatedAt: new Date().toISOString(),
          _optimistic: true,
        }),
      );

      return { previousData };
    },
    onSuccess: ({ result }) => {
      queryClient.setQueryData(queryKey, (old) =>
        patchWord(old, result._id, { ...result, _optimistic: false }),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteWordMutation = useMutation({
    mutationFn: (id) => deleteWord(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => removeWords(old, [id]));

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    wordBankQuery,
    createWordsMutation,
    updateWordMutation,
    deleteWordMutation,
  };
};
